import React, { useState } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner, Badge, Tab, Tabs, Modal, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface ProfileFormData {
  name: string;
  profile: {
    phone: string;
    dateOfBirth: string;
    gender: string;
    address: {
      street: string;
      city: string;
      state: string;
      pincode: string;
      country: string;
    };
  };
  bankDetails: {
    accountNumber: string;
    confirmAccountNumber: string;
    ifscCode: string;
    bankName: string;
    accountHolderName: string;
    accountType: string;
  };
  kycDetails: {
    panNumber: string;
  };
}

// Helper function to format date for HTML date input
const formatDateForInput = (dateString: string | undefined | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

// Helper function to check if field should be locked (has saved value from DB and user is not admin)
const isFieldLocked = (fieldPath: string, userRole: string, isProfileLocked: boolean, user: any): boolean => {
  if (userRole === 'admin') return false; // Admins can always edit
  
  // Special case for PAN field - don't lock if it's empty or just placeholder/default values
  if (fieldPath === 'kycDetails.panNumber') {
    const panValue = user?.kycDetails?.panNumber;
    // Always allow PAN entry if it's empty, regardless of profile lock status
    if (!panValue || panValue.trim() === '' || panValue.includes('ABCDE') || panValue.includes('1234F')) {
      return false;
    }
    // If PAN has a real value, then apply normal locking rules
    return isProfileLocked;
  }
  
  if (isProfileLocked) return true; // Profile is globally locked
  
  // Get the original value from user data (from DB), not current form data
  const pathParts = fieldPath.split('.');
  let originalValue = user;
  
  for (const part of pathParts) {
    if (originalValue && typeof originalValue === 'object') {
      originalValue = originalValue[part as keyof typeof originalValue];
    } else {
      originalValue = null;
      break;
    }
  }
  
  // Check if original value from DB exists and is not empty
  return originalValue && originalValue.toString().trim() !== '';
};

// Helper function to check if KYC form should be submittable
const canSubmitKYC = (userRole: string, isProfileLocked: boolean, user: any): boolean => {
  if (userRole === 'admin') return true; // Admins can always submit
  if (!isProfileLocked) return true; // If profile isn't locked, allow submission
  
  // If profile is locked, only allow submission if PAN is empty (can be entered)
  const panValue = user?.kycDetails?.panNumber;
  return !panValue || panValue.trim() === '';
};

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('personal');
  const [formData, setFormData] = useState<ProfileFormData>({
    name: user?.name || '',
    profile: {
      phone: user?.profile?.phone || '',
      dateOfBirth: formatDateForInput(user?.profile?.dateOfBirth),
      gender: user?.profile?.gender || '',
      address: {
        street: (typeof user?.profile?.address === 'object' ? user.profile.address.street : '') || '',
        city: (typeof user?.profile?.address === 'object' ? user.profile.address.city : '') || '',
        state: (typeof user?.profile?.address === 'object' ? user.profile.address.state : '') || '',
        pincode: (typeof user?.profile?.address === 'object' ? user.profile.address.pincode : '') || '',
        country: (typeof user?.profile?.address === 'object' ? user.profile.address.country : '') || 'India'
      },
    },
    bankDetails: {
      accountNumber: user?.bankDetails?.accountNumber || '',
      confirmAccountNumber: '',
      ifscCode: user?.bankDetails?.ifscCode || '',
      bankName: user?.bankDetails?.bankName || '',
      accountHolderName: user?.bankDetails?.accountHolderName || '',
      accountType: user?.bankDetails?.accountType || ''
    },
    kycDetails: {
      panNumber: user?.kycDetails?.panNumber || ''
    }
  });

  // Document upload state
  const [selectedFiles, setSelectedFiles] = useState<{[key: string]: File}>({});
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentDocType, setCurrentDocType] = useState('');

  // Form states
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Modal-specific error and success states  
  const [modalError, setModalError] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [isProfileLocked, setIsProfileLocked] = useState(user?.profileLocked || false);

  // Debug PAN field locking - more comprehensive debugging
  const panValue = user?.kycDetails?.panNumber;
  const panLocked = isFieldLocked('kycDetails.panNumber', user?.role || '', isProfileLocked, user);
  console.log('PAN Debug Info:', {
    userPanNumber: panValue,
    isPanEmpty: !panValue || panValue.trim() === '',
    isPlaceholder: panValue?.includes('ABCDE') || panValue?.includes('1234F'),
    isPanLocked: panLocked,
    userRole: user?.role,
    isProfileLocked: isProfileLocked,
    canSubmitKYC: canSubmitKYC(user?.role || '', isProfileLocked, user)
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // Check if profile is locked on component mount and when user data changes
  React.useEffect(() => {
    if (user?.profileLocked) {
      setIsProfileLocked(true);
    } else {
      setIsProfileLocked(false);
    }
    
    // Force re-evaluation of field states by triggering a re-render
    // This ensures PAN field locking is properly recalculated
    console.log('Profile lock status updated:', user?.profileLocked);
  }, [user, user?.profileLocked, user?.kycDetails?.panNumber]);

  // Update form data when user data changes
  React.useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        profile: {
          phone: user.profile?.phone || '',
          dateOfBirth: formatDateForInput(user.profile?.dateOfBirth),
          gender: user.profile?.gender || '',
          address: {
            street: (typeof user.profile?.address === 'object' ? user.profile.address.street : '') || '',
            city: (typeof user.profile?.address === 'object' ? user.profile.address.city : '') || '',
            state: (typeof user.profile?.address === 'object' ? user.profile.address.state : '') || '',
            pincode: (typeof user.profile?.address === 'object' ? user.profile.address.pincode : '') || '',
            country: (typeof user.profile?.address === 'object' ? user.profile.address.country : '') || 'India'
          },
        },
        bankDetails: {
          accountNumber: user.bankDetails?.accountNumber || '',
          confirmAccountNumber: '',
          ifscCode: user.bankDetails?.ifscCode || '',
          bankName: user.bankDetails?.bankName || '',
          accountHolderName: user.bankDetails?.accountHolderName || '',
          accountType: user.bankDetails?.accountType || ''
        },
        kycDetails: {
          panNumber: user.kycDetails?.panNumber || ''
        }
      });
    }
  }, [user]);

  // Profile completion calculation
  const calculateProfileCompletion = () => {
    const fields = [
      formData.name,
      formData.profile.phone,
      formData.profile.dateOfBirth,
      formData.profile.gender,
      formData.profile.address.street,
      formData.profile.address.city,
      formData.profile.address.state,
      formData.profile.address.pincode,
      formData.bankDetails.accountNumber,
      formData.bankDetails.ifscCode,
      formData.bankDetails.bankName,
      formData.bankDetails.accountHolderName,
      formData.kycDetails.panNumber
    ];
    
    const completedFields = fields.filter(field => field && field.toString().trim()).length;
    return Math.round((completedFields / fields.length) * 100);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.includes('profile.address.')) {
      const field = name.split('.')[2];
      setFormData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          address: {
            ...prev.profile.address,
            [field]: value
          }
        }
      }));
    } else if (name.includes('profile.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          [field]: value
        }
      }));
    } else if (name.includes('bankDetails.')) {
      const field = name.split('.')[1];
      // Convert IFSC code to uppercase
      const processedValue = field === 'ifscCode' ? value.toUpperCase() : value;
      setFormData(prev => ({
        ...prev,
        bankDetails: {
          ...prev.bankDetails,
          [field]: processedValue
        }
      }));
    } else if (name.includes('kycDetails.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        kycDetails: {
          ...prev.kycDetails,
          [field]: value.toUpperCase()
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePersonalInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    try {
      const response = await axios.put(`${API_BASE_URL}/users/profile`, {
        name: formData.name,
        profile: formData.profile
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        setMessage(response.data.message);
        updateUser(response.data.user);
        
        // Update profile locked status
        if (response.data.profileLocked) {
          setIsProfileLocked(true);
        }
      } else {
        setError(response.data.message || 'Failed to update personal information');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update personal information');
    } finally {
      setLoading(false);
    }
  };

  const handleBankDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');

    // Validate account number match
    if (formData.bankDetails.accountNumber !== formData.bankDetails.confirmAccountNumber) {
      setError('Account numbers do not match. Please verify and try again.');
      return;
    }

    setLoading(true);

    try {
      // Remove confirmAccountNumber from data sent to backend
      const { confirmAccountNumber, ...bankDetailsToSend } = formData.bankDetails;
      
      const response = await axios.put(`${API_BASE_URL}/users/bank-details`, bankDetailsToSend, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        setMessage(response.data.message);
        updateUser(response.data.user);
        
        // Update profile locked status
        if (response.data.profileLocked) {
          setIsProfileLocked(true);
        }
        
        // Log for debugging - check if PAN should still be editable
        console.log('Bank details saved. Profile locked:', response.data.profileLocked);
        console.log('PAN value after bank update:', response.data.user?.kycDetails?.panNumber);
      } else {
        setError(response.data.message || 'Failed to update bank details');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update bank details');
    } finally {
      setLoading(false);
    }
  };

  const handleKYCSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    try {
      const response = await axios.put(`${API_BASE_URL}/users/kyc-details`, formData.kycDetails, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        setMessage(response.data.message);
        updateUser(response.data.user);
        
        // Don't update profile locked status for KYC updates
        // The profile might be locked but we allow PAN entry when it's empty
      } else {
        setError(response.data.message || 'Failed to update KYC details');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update KYC details');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFiles(prev => ({
        ...prev,
        [currentDocType]: file
      }));
    }
  };

  const handleClearFile = (docType: string) => {
    setSelectedFiles(prev => {
      const updated = { ...prev };
      delete updated[docType];
      return updated;
    });
    
    // Clear the file input
    const fileInput = document.querySelector(`input[type="file"]`) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleDocumentUpload = async () => {
    const file = selectedFiles[currentDocType];
    if (!file) return;

    setLoading(true);
    setModalError('');
    setModalMessage('');
    
    const formData = new FormData();
    formData.append('document', file);
    formData.append('type', currentDocType);

    try {
      const response = await axios.post(`${API_BASE_URL}/users/upload-document`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(prev => ({
            ...prev,
            [currentDocType]: progress
          }));
        }
      });

      if (response.data.success) {
        setModalMessage('Document uploaded successfully!');
        setTimeout(() => {
          setShowUploadModal(false);
          setModalMessage('');
        }, 2000);
        updateUser(response.data.user);
      } else {
        setModalError('Failed to upload document');
      }
    } catch (error: any) {
      setModalError(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const getUploadedDocument = (docType: string) => {
    return user?.kycDetails?.documents?.find(doc => doc.type === docType);
  };

  const getDocumentPreviewUrl = (filename: string) => {
    return `${API_BASE_URL}/users/document/${filename}`;
  };

  // Component to handle image loading with authorization
  const DocumentImagePreview: React.FC<{ document: any }> = ({ document }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    React.useEffect(() => {
      const loadImage = async () => {
        try {
          const response = await fetch(getDocumentPreviewUrl(document.filename), {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setImageUrl(url);
          } else {
            setError(true);
          }
        } catch (err) {
          setError(true);
        } finally {
          setLoading(false);
        }
      };

      loadImage();

      // Cleanup
      return () => {
        if (imageUrl) {
          URL.revokeObjectURL(imageUrl);
        }
      };
    }, [document.filename]);

    if (loading) {
      return (
        <div className="mt-2 text-center p-3">
          <Spinner size="sm" />
          <div className="mt-1">
            <small className="text-muted">Loading image...</small>
          </div>
        </div>
      );
    }

    if (error || !imageUrl) {
      return (
        <div className="mt-2">
          <div className="border rounded p-3 text-center text-muted">
            <i className="fas fa-image" style={{ fontSize: '2rem' }}></i>
            <div className="mt-1">
              <small>Image preview not available</small>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-2">
        <img 
          src={imageUrl}
          alt="Document preview"
          className="img-fluid rounded border"
          style={{ maxHeight: '200px', maxWidth: '100%' }}
        />
      </div>
    );
  };

  const renderDocumentPreview = (document: any) => {
    if (!document) return null;
    
    const isImage = document.mimeType?.startsWith('image/');
    const isPdf = document.mimeType === 'application/pdf';
    
    if (isImage) {
      return <DocumentImagePreview document={document} />;
    } else if (isPdf) {
      return (
        <div className="mt-2">
          <div className="border rounded p-3 text-center">
            <i className="fas fa-file-pdf text-danger" style={{ fontSize: '3rem' }}></i>
            <div className="mt-2">
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => {
                  fetch(getDocumentPreviewUrl(document.filename), {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                  })
                  .then(response => response.blob())
                  .then(blob => {
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                  })
                  .catch(error => {
                    console.error('Error opening PDF:', error);
                    alert('Error opening PDF file');
                  });
                }}
              >
                <i className="fas fa-eye me-1"></i>
                View PDF
              </Button>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="mt-2">
        <div className="border rounded p-3 text-center">
          <i className="fas fa-file text-secondary" style={{ fontSize: '3rem' }}></i>
          <div className="mt-2">
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => {
                fetch(getDocumentPreviewUrl(document.filename), {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                })
                .then(response => response.blob())
                .then(blob => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = document.originalName;
                  a.click();
                  URL.revokeObjectURL(url);
                })
                .catch(error => {
                  console.error('Error downloading file:', error);
                  alert('Error downloading file');
                });
              }}
            >
              <i className="fas fa-download me-1"></i>
              Download
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const openUploadModal = (docType: string) => {
    setCurrentDocType(docType);
    setModalError('');
    setModalMessage('');
    setShowUploadModal(true);
  };

  const getVerificationStatus = (status: string) => {
    switch (status) {
      case 'verified':
        return <Badge bg="success">Verified</Badge>;
      case 'under_review':
        return <Badge bg="warning">Under Review</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">Not Verified</Badge>;
    }
  };

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1>Profile Management</h1>
              <p className="text-muted">Complete your profile to unlock all features</p>
            </div>
            <div className="text-end">
              <div className="mb-2">
                <small className="text-muted">Profile Completion</small>
              </div>
              <ProgressBar 
                now={calculateProfileCompletion()} 
                label={`${calculateProfileCompletion()}%`}
                style={{ width: '150px' }}
              />
            </div>
          </div>
        </Col>
      </Row>

      {user?.role === 'admin' && (
        <Alert variant="info" className="mb-3">
          <i className="fas fa-crown me-2"></i>
          <strong>Admin Access:</strong> You can edit all fields regardless of their lock status.
        </Alert>
      )}

      {isProfileLocked && (
        <Alert variant="warning" className="mb-3">
          <i className="fas fa-lock me-2"></i>
          <strong>Profile Locked:</strong> Your profile has been locked to prevent accidental changes. 
          {user?.role !== 'admin' && 'Contact support if you need to make changes.'}
        </Alert>
      )}

      {message && (
        <Row className="mb-4">
          <Col>
            <Alert variant="success" dismissible onClose={() => setMessage('')}>
              <i className="fas fa-check-circle me-2"></i>
              {message}
            </Alert>
          </Col>
        </Row>
      )}

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Card>
        <Card.Body>
          <Tabs activeKey={activeTab} onSelect={(tab) => setActiveTab(tab || 'personal')} className="mb-4">
            <Tab eventKey="personal" title={
              <span>
                <i className="fas fa-user me-2"></i>
                Personal Info
              </span>
            }>
              <Form onSubmit={handlePersonalInfoSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        disabled={loading || isFieldLocked('name', user?.role || '', isProfileLocked, user)}
                      />
                      {isFieldLocked('name', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        value={user?.email || ''}
                        disabled
                      />
                      <Form.Text className="text-muted">
                        Email cannot be changed. Contact support if needed.
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number *</Form.Label>
                      <Form.Control
                        type="tel"
                        name="profile.phone"
                        value={formData.profile.phone}
                        onChange={handleInputChange}
                        placeholder="10-digit mobile number"
                        pattern="[6-9][0-9]{9}"
                        disabled={loading || isFieldLocked('profile.phone', user?.role || '', isProfileLocked, user)}
                      />
                      {isFieldLocked('profile.phone', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Date of Birth</Form.Label>
                      <Form.Control
                        type="date"
                        name="profile.dateOfBirth"
                        value={formData.profile.dateOfBirth}
                        onChange={handleInputChange}
                        disabled={loading || isFieldLocked('profile.dateOfBirth', user?.role || '', isProfileLocked, user)}
                      />
                      {isFieldLocked('profile.dateOfBirth', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Gender</Form.Label>
                      <Form.Select
                        name="profile.gender"
                        value={formData.profile.gender}
                        onChange={handleInputChange}
                        disabled={loading || isFieldLocked('profile.gender', user?.role || '', isProfileLocked, user)}
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </Form.Select>
                      {isFieldLocked('profile.gender', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <h6 className="mb-3">Address Information</h6>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Street Address</Form.Label>
                      <Form.Control
                        type="text"
                        name="profile.address.street"
                        value={formData.profile.address.street}
                        onChange={handleInputChange}
                        placeholder="House no, Street name"
                        disabled={loading || isFieldLocked('profile.address.street', user?.role || '', isProfileLocked, user)}
                      />
                      {isFieldLocked('profile.address.street', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>City</Form.Label>
                          <Form.Control
                            type="text"
                            name="profile.address.city"
                            value={formData.profile.address.city}
                            onChange={handleInputChange}
                            disabled={loading || isFieldLocked('profile.address.city', user?.role || '', isProfileLocked, user)}
                          />
                          {isFieldLocked('profile.address.city', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                            <Form.Text className="text-muted">
                              <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>State</Form.Label>
                          <Form.Control
                            type="text"
                            name="profile.address.state"
                            value={formData.profile.address.state}
                            onChange={handleInputChange}
                            disabled={loading || isFieldLocked('profile.address.state', user?.role || '', isProfileLocked, user)}
                          />
                          {isFieldLocked('profile.address.state', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                            <Form.Text className="text-muted">
                              <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Pincode</Form.Label>
                          <Form.Control
                            type="text"
                            name="profile.address.pincode"
                            value={formData.profile.address.pincode}
                            onChange={handleInputChange}
                            placeholder="6-digit pincode"
                            pattern="[1-9][0-9]{5}"
                            disabled={loading || isFieldLocked('profile.address.pincode', user?.role || '', isProfileLocked, user)}
                          />
                          {isFieldLocked('profile.address.pincode', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                            <Form.Text className="text-muted">
                              <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Country</Form.Label>
                          <Form.Control
                            type="text"
                            name="profile.address.country"
                            value={formData.profile.address.country}
                            onChange={handleInputChange}
                            disabled={loading || isProfileLocked}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <div className="text-end">
                  <Button type="submit" variant="primary" disabled={loading || isProfileLocked}>
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : isProfileLocked ? (
                      <>
                        <i className="fas fa-lock me-2"></i>
                        Profile Locked
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Update Personal Info
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Tab>

            <Tab eventKey="bank" title={
              <span>
                <i className="fas fa-university me-2"></i>
                Bank Details
                {user?.bankDetails?.isVerified && <Badge bg="success" className="ms-2">Verified</Badge>}
              </span>
            }>
              <Form onSubmit={handleBankDetailsSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Holder Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankDetails.accountHolderName"
                        value={formData.bankDetails.accountHolderName}
                        onChange={handleInputChange}
                        placeholder="As per bank records"
                        required
                        disabled={loading || isFieldLocked('bankDetails.accountHolderName', user?.role || '', isProfileLocked, user)}
                      />
                      {isFieldLocked('bankDetails.accountHolderName', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Account Number *</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankDetails.accountNumber"
                        value={formData.bankDetails.accountNumber}
                        onChange={handleInputChange}
                        placeholder="Enter account number"
                        required
                        disabled={loading || isFieldLocked('bankDetails.accountNumber', user?.role || '', isProfileLocked, user)}
                      />
                      {isFieldLocked('bankDetails.accountNumber', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Confirm Account Number *</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankDetails.confirmAccountNumber"
                        value={formData.bankDetails.confirmAccountNumber}
                        onChange={handleInputChange}
                        placeholder="Re-enter account number to confirm"
                        required
                        disabled={loading || isProfileLocked}
                      />
                      {formData.bankDetails.confirmAccountNumber && 
                       formData.bankDetails.accountNumber !== formData.bankDetails.confirmAccountNumber && (
                        <Form.Text className="text-danger">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          Account numbers do not match
                        </Form.Text>
                      )}
                      {formData.bankDetails.confirmAccountNumber && 
                       formData.bankDetails.accountNumber === formData.bankDetails.confirmAccountNumber && (
                        <Form.Text className="text-success">
                          <i className="fas fa-check-circle me-1"></i>
                          Account numbers match
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>IFSC Code *</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankDetails.ifscCode"
                        value={formData.bankDetails.ifscCode}
                        onChange={handleInputChange}
                        placeholder="e.g., HDFC0001234"
                        pattern="[A-Za-z]{4}[0-9][A-Za-z0-9]{6}"
                        minLength={11}
                        maxLength={11}
                        style={{ textTransform: 'uppercase' }}
                        required
                        disabled={loading || isFieldLocked('bankDetails.ifscCode', user?.role || '', isProfileLocked, user)}
                      />
                      {isFieldLocked('bankDetails.ifscCode', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' ? (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      ) : (
                        <Form.Text className="text-muted">
                          11-character IFSC code of your bank
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bank Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankDetails.bankName"
                        value={formData.bankDetails.bankName}
                        onChange={handleInputChange}
                        placeholder="e.g., State Bank of India"
                        required
                        disabled={loading || isFieldLocked('bankDetails.bankName', user?.role || '', isProfileLocked, user)}
                      />
                      {isFieldLocked('bankDetails.bankName', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Account Type</Form.Label>
                      <Form.Select
                        name="bankDetails.accountType"
                        value={formData.bankDetails.accountType}
                        onChange={handleInputChange}
                        disabled={loading || isFieldLocked('bankDetails.accountType', user?.role || '', isProfileLocked, user)}
                      >
                        <option value="">Select Account Type</option>
                        <option value="savings">Savings</option>
                        <option value="current">Current</option>
                      </Form.Select>
                      {isFieldLocked('bankDetails.accountType', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      )}
                    </Form.Group>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Verification Status:</span>
                        {getVerificationStatus(user?.bankDetails?.status || 'not_verified')}
                      </div>
                    </div>
                  </Col>
                </Row>

                <Alert variant="info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Security Note:</strong> Your bank details are encrypted and secure. 
                  They will be verified by our team before enabling withdrawals.
                </Alert>

                <div className="text-end">
                  <Button type="submit" variant="primary" disabled={loading || isProfileLocked}>
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : isProfileLocked ? (
                      <>
                        <i className="fas fa-lock me-2"></i>
                        Profile Locked
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Update Bank Details
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Tab>

            <Tab eventKey="kyc" title={
              <span>
                <i className="fas fa-id-card me-2"></i>
                KYC Documents
                {user?.kycDetails?.isVerified && <Badge bg="success" className="ms-2">Verified</Badge>}
              </span>
            }>
              <Form onSubmit={handleKYCSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>PAN Number *</Form.Label>
                      <Form.Control
                        type="text"
                        name="kycDetails.panNumber"
                        value={formData.kycDetails.panNumber}
                        onChange={handleInputChange}
                        placeholder="e.g., ABCDE1234F"
                        style={{ textTransform: 'uppercase' }}
                        maxLength={10}
                        required
                        disabled={loading || isFieldLocked('kycDetails.panNumber', user?.role || '', isProfileLocked, user)}
                      />
                      {isFieldLocked('kycDetails.panNumber', user?.role || '', isProfileLocked, user) && user?.role !== 'admin' && (
                        <Form.Text className="text-muted">
                          <i className="fas fa-lock"></i> This field is locked. Contact admin to make changes.
                        </Form.Text>
                      )}
                      {!isFieldLocked('kycDetails.panNumber', user?.role || '', isProfileLocked, user) && isProfileLocked && (
                        <Form.Text className="text-success">
                          <i className="fas fa-info-circle"></i> You can still enter your PAN number even though your profile is locked.
                        </Form.Text>
                      )}
                    </Form.Group>


                  </Col>

                  <Col md={6}>
                    <h6 className="mb-3">Document Upload</h6>
                    
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>PAN Card Document:</span>
                        {getUploadedDocument('pan_card') && (
                          <Badge bg={getUploadedDocument('pan_card')?.isVerified ? 'success' : 'warning'}>
                            {getUploadedDocument('pan_card')?.isVerified ? 'Verified' : 'Under Review'}
                          </Badge>
                        )}
                      </div>
                      
                      {getUploadedDocument('pan_card') ? (
                        <div className="border rounded p-3 mb-2">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <i className="fas fa-file-alt me-2 text-primary"></i>
                              <span className="fw-bold">{getUploadedDocument('pan_card')?.originalName}</span>
                              <br />
                              <small className="text-muted">
                                Uploaded: {new Date(getUploadedDocument('pan_card')?.uploadDate || '').toLocaleDateString()}
                              </small>
                            </div>
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              onClick={() => openUploadModal('pan_card')}
                            >
                              <i className="fas fa-edit me-1"></i>
                              Replace
                            </Button>
                          </div>
                          {renderDocumentPreview(getUploadedDocument('pan_card'))}
                        </div>
                      ) : (
                        <Button 
                          variant="outline-primary" 
                          onClick={() => openUploadModal('pan_card')}
                          className="me-2 mb-2"
                        >
                          <i className="fas fa-upload me-2"></i>
                          Upload PAN Card
                        </Button>
                      )}


                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>KYC Status:</span>
                        {getVerificationStatus(user?.kycDetails?.status || 'not_started')}
                      </div>
                    </div>
                  </Col>
                </Row>

                <Alert variant="warning">
                  <i className="fas fa-shield-alt me-2"></i>
                  <strong>Important:</strong> KYC verification is mandatory for reward withdrawals. 
                  All documents are securely stored and reviewed by our compliance team.
                </Alert>

                <div className="text-end">
                  <Button type="submit" variant="primary" disabled={loading || !canSubmitKYC(user?.role || '', isProfileLocked, user)}>
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : !canSubmitKYC(user?.role || '', isProfileLocked, user) ? (
                      <>
                        <i className="fas fa-lock me-2"></i>
                        Profile Locked
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Update KYC Details
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* Document Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Error Alert Inside Modal */}
          {modalError && (
            <Alert variant="danger" dismissible onClose={() => setModalError('')} className="mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {modalError}
            </Alert>
          )}
          
          {/* Success Alert Inside Modal */}
          {modalMessage && (
            <Alert variant="success" dismissible onClose={() => setModalMessage('')} className="mb-3">
              <i className="fas fa-check-circle me-2"></i>
              {modalMessage}
            </Alert>
          )}
          
          <Form.Group className="mb-3">
            <Form.Label>Select {currentDocType.replace('_', ' ').toUpperCase()} document</Form.Label>
            <Form.Control
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileSelect}
            />
            {selectedFiles[currentDocType] && (
              <div className="mt-2">
                <small className="text-muted d-block">
                  Selected: {selectedFiles[currentDocType].name}
                </small>
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => handleClearFile(currentDocType)}
                >
                  <i className="fas fa-times me-1"></i>
                  Clear File
                </Button>
              </div>
            )}
            <Form.Text className="text-muted">
              Accepted formats: JPG, PNG, PDF (Max 5MB)
            </Form.Text>
          </Form.Group>

          {uploadProgress[currentDocType] > 0 && (
            <ProgressBar 
              now={uploadProgress[currentDocType]} 
              label={`${uploadProgress[currentDocType]}%`}
              className="mb-3"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleDocumentUpload}
            disabled={!selectedFiles[currentDocType] || loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              <>
                <i className="fas fa-upload me-2"></i>
                Upload Document
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Profile;